import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaCamera,
  FaCheckCircle,
  FaDownload,
  FaExclamationCircle,
  FaGraduationCap,
  FaHashtag,
  FaImage,
  FaSearch,
  FaSyncAlt,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import JSZip from "jszip";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import MdlEditStudentImage from "../dialog/MdlEditStudentImage";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "with", label: "With photo" },
  { id: "without", label: "No photo" },
];

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function studentKey(student) {
  return String(student?.student_enrollment_no || student?.id || "");
}

//Created By Cursor — candidate URL only (empty / host-only rejected)
function getCandidatePhotoUrl(raw) {
  const path = String(raw || "").trim();
  if (!path) return "";

  const lower = path.toLowerCase();
  if (
    lower === "null" ||
    lower === "undefined" ||
    lower === "n/a" ||
    lower === "none" ||
    lower === "-" ||
    lower === "/"
  ) {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      const pathname = String(u.pathname || "").replace(/\/+$/, "");
      if (!pathname) return "";
      return path;
    } catch {
      return "";
    }
  }

  const cleaned = path.replace(/\\/g, "/").replace(/\/+$/, "");
  if (!cleaned) return "";
  return path;
}

function photoSrc(student, cacheKey) {
  const path = getCandidatePhotoUrl(student?.student_img_path);
  if (!path) return "";
  return `${path}${path.includes("?") ? "&" : "?"}v=${cacheKey}`;
}

function safeZipFileName(student) {
  const enroll = String(student?.student_enrollment_no || "student")
    .trim()
    .replace(/[^\w.-]+/g, "_");
  const name = String(student?.stu_name || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w.-]+/g, "");
  const base = name ? `${enroll}_${name}` : enroll;
  return base.slice(0, 80) || "student";
}

function extFromMimeOrUrl(mime, url) {
  if (mime && mime.includes("png")) return "png";
  if (mime && mime.includes("webp")) return "webp";
  if (mime && mime.includes("gif")) return "gif";
  const path = String(url || "").split("?")[0];
  const m = /\.(jpe?g|png|gif|webp|bmp|jfif)$/i.exec(path);
  if (m) return m[1].toLowerCase().replace("jpeg", "jpg").replace("jfif", "jpg");
  return "jpg";
}

async function fetchStudentPhotoBlob(student, cacheKey) {
  const enroll = student?.student_enrollment_no;
  const schoolCode = SessionManager.getSchoolCode() || "";
  const directUrl = photoSrc(student, cacheKey);

  if (enroll && schoolCode) {
    try {
      let proxyUrl = `${API_BASE()}getStudentImageBlob.php?enrollment_no=${encodeURIComponent(
        enroll
      )}&school_code=${encodeURIComponent(schoolCode)}`;
      if (cacheKey != null) {
        proxyUrl += `&v=${encodeURIComponent(String(cacheKey))}`;
      }
      const { data } = await axios.get(proxyUrl, {
        responseType: "blob",
        withCredentials: true,
      });
      if (data instanceof Blob && data.size > 0) {
        if (
          data.type.startsWith("image/") ||
          !data.type ||
          data.type === "application/octet-stream"
        ) {
          return data;
        }
      }
    } catch (e) {
      // fall through to direct URL
    }
  }

  if (!directUrl) throw new Error("No photo URL");
  const { data } = await axios.get(directUrl, {
    responseType: "blob",
    withCredentials: true,
  });
  if (!(data instanceof Blob) || data.size === 0) {
    throw new Error("Empty photo");
  }
  return data;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

//Created By Cursor — verify the URL actually loads as an image
function probeImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    const img = new Image();
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), 10000);
    img.onload = () => {
      clearTimeout(timer);
      finish(Boolean(img.naturalWidth > 0 && img.naturalHeight > 0));
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish(false);
    };
    img.src = url;
  });
}

const StudentPhotoCard = memo(function StudentPhotoCard({
  student,
  index,
  cacheKey,
  withPhoto,
  onEdit,
  onPhotoResult,
}) {
  const src = photoSrc(student, cacheKey);
  const key = studentKey(student);
  const showImg = Boolean(src) && withPhoto !== false;

  return (
    <button
      type="button"
      className={`msi-card${withPhoto ? " msi-card--ok" : " msi-card--miss"}`}
      style={{ "--msi-i": index }}
      onClick={() => onEdit(student)}
      title={`Edit photo — ${student.stu_name || ""}`}
    >
      <div className="msi-card__shell">
        <div className="msi-card__frame">
          {showImg ? (
            <img
              src={src}
              alt=""
              className="msi-card__img"
              onLoad={() => {
                if (onPhotoResult) onPhotoResult(key, true);
              }}
              onError={() => {
                if (onPhotoResult) onPhotoResult(key, false);
              }}
            />
          ) : (
            <span className="msi-card__placeholder" aria-hidden>
              <span className="msi-card__initials">
                {studentInitials(student.stu_name)}
              </span>
            </span>
          )}
          <span
            className={`msi-card__badge${
              withPhoto ? " msi-card__badge--ok" : " msi-card__badge--miss"
            }`}
          >
            {withPhoto ? (
              <>
                <FaCheckCircle aria-hidden /> Photo
              </>
            ) : (
              <>
                <FaExclamationCircle aria-hidden /> Missing
              </>
            )}
          </span>
          <span className="msi-card__overlay">
            <span className="msi-card__overlay-btn">
              <FaCamera aria-hidden />
              <span>{withPhoto ? "Edit photo" : "Add photo"}</span>
            </span>
          </span>
        </div>
        <div className="msi-card__meta">
          <span className="msi-card__name" title={student.stu_name || ""}>
            {student.stu_name || "—"}
          </span>
          {student.father_name ? (
            <span className="msi-card__father" title={student.father_name}>
              S/O {student.father_name}
            </span>
          ) : null}
          <div className="msi-card__chips">
            {student.student_roll_no ? (
              <span className="msi-chip">
                <FaHashtag aria-hidden /> {student.student_roll_no}
              </span>
            ) : null}
            {student.student_enrollment_no ? (
              <span className="msi-chip msi-chip--muted">
                ID {student.student_enrollment_no}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
});

//Created By Cursor
export default function ManageStudentImage() {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [photoFilter, setPhotoFilter] = useState("all");
  const [cacheKey, setCacheKey] = useState(0);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  //Created By Cursor — true only after image URL actually loads
  const [photoOkMap, setPhotoOkMap] = useState({});
  const [photoCheckDone, setPhotoCheckDone] = useState(false);
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    setLoadClass(true);
  }, []);

  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          class_id: classId,
          section_id: sectionId,
          session_id: sessionId,
        };

        let payload = null;
        try {
          const res = await axios.get(
            `${API_BASE()}getCurrentStudentForManageRecords.php`,
            { params }
          );
          payload = res?.data;
        } catch (primaryError) {
          console.warn(
            "[ManageStudentImage] manage-records endpoint failed, falling back",
            primaryError
          );
        }

        if (!payload || payload.api_status !== "OK") {
          const res = await axios.get(`${API_BASE()}getCurrentStudent.php`, {
            params,
          });
          payload = res?.data;
        }

        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          setStudents(payload.data);
          setPhotoOkMap({});
          setPhotoCheckDone(false);
        } else {
          setStudents([]);
          setPhotoOkMap({});
          setPhotoCheckDone(true);
        }
      } catch (error) {
        console.error("[ManageStudentImage] load error", error);
        setStudents([]);
        myToast.notify_warning("Failed to load students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  //Created By Cursor — probe every candidate URL so counts match real images
  useEffect(() => {
    let cancelled = false;

    if (!students.length) {
      setPhotoOkMap({});
      setPhotoCheckDone(true);
      return undefined;
    }

    setPhotoCheckDone(false);

    const run = async () => {
      const next = {};
      const jobs = students.map(async (student) => {
        const key = studentKey(student);
        if (!key) return;
        const url = photoSrc(student, cacheKey);
        if (!url) {
          next[key] = false;
          return;
        }
        const ok = await probeImage(url);
        next[key] = ok;
      });

      await Promise.all(jobs);
      if (cancelled) return;
      setPhotoOkMap(next);
      setPhotoCheckDone(true);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [students, cacheKey]);

  const onClassSelect = useCallback(
    (nextClassId, nextSectionId, nextSessionId, position, className, sectionName) => {
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedClassPosition(position);
      setClassId(nextClassId);
      setSearchTerm("");
      setPhotoFilter("all");
      setPhotoOkMap({});
      setPhotoCheckDone(false);
    },
    []
  );

  const studentHasPhoto = useCallback(
    (student) => {
      const key = studentKey(student);
      if (!key) return false;
      if (Object.prototype.hasOwnProperty.call(photoOkMap, key)) {
        return photoOkMap[key] === true;
      }
      return false;
    },
    [photoOkMap]
  );

  const handlePhotoResult = useCallback((key, ok) => {
    if (!key) return;
    setPhotoOkMap((prev) => {
      if (prev[key] === ok) return prev;
      return { ...prev, [key]: ok };
    });
  }, []);

  const metrics = useMemo(() => {
    const withCount = students.filter(studentHasPhoto).length;
    const total = students.length;
    const pct = total > 0 ? Math.round((withCount / total) * 100) : 0;
    return {
      total,
      withPhoto: withCount,
      withoutPhoto: total - withCount,
      pct,
    };
  }, [students, studentHasPhoto]);

  const filterCounts = useMemo(
    () => ({
      all: students.length,
      with: students.filter(studentHasPhoto).length,
      without: students.filter((s) => !studentHasPhoto(s)).length,
    }),
    [students, studentHasPhoto]
  );

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return students.filter((s) => {
      if (photoFilter === "with" && !studentHasPhoto(s)) return false;
      if (photoFilter === "without" && studentHasPhoto(s)) return false;
      if (!q) return true;
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, searchTerm, photoFilter, studentHasPhoto]);

  const openEditor = useCallback((student) => {
    setEditingStudent(student);
    setEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingStudent(null);
  }, []);

  const handleSaved = useCallback(() => {
    setCacheKey((k) => k + 1);
    loadStudents({ soft: true });
  }, [loadStudents]);

  //Created By Cursor — download verified photos as a ZIP
  const downloadPhotosZip = useCallback(async () => {
    const withPhotos = students.filter(studentHasPhoto);
    if (!withPhotos.length) {
      myToast.notify_warning("No photos available to download.");
      return;
    }

    setZipping(true);
    try {
      const zip = new JSZip();
      const folderLabel = String(classSectionName || "photos")
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, "_")
        .slice(0, 60);
      const folder = zip.folder(folderLabel || "student_photos");
      let added = 0;
      const usedNames = new Set();

      for (const student of withPhotos) {
        try {
          const blob = await fetchStudentPhotoBlob(student, cacheKey);
          const ext = extFromMimeOrUrl(blob.type, student.student_img_path);
          let base = safeZipFileName(student);
          let fileName = `${base}.${ext}`;
          let n = 2;
          while (usedNames.has(fileName.toLowerCase())) {
            fileName = `${base}_${n}.${ext}`;
            n += 1;
          }
          usedNames.add(fileName.toLowerCase());
          folder.file(fileName, blob);
          added += 1;
        } catch (err) {
          console.warn(
            "[ManageStudentImage] zip skip",
            student.student_enrollment_no,
            err
          );
        }
      }

      if (!added) {
        myToast.notify_warning("Could not fetch any photos for the ZIP.");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        zipBlob,
        `${folderLabel || "student_photos"}_${stamp}.zip`
      );
      myToast.notify_success(`Downloaded ${added} photo${added === 1 ? "" : "s"} as ZIP.`);
    } catch (error) {
      console.error("[ManageStudentImage] zip error", error);
      myToast.notify_warning("Failed to create ZIP download.");
    } finally {
      setZipping(false);
    }
  }, [students, studentHasPhoto, classSectionName, cacheKey]);

  const editingImageUrl = useMemo(() => {
    if (!editingStudent) return "";
    const candidate = getCandidatePhotoUrl(editingStudent.student_img_path);
    if (candidate) {
      return `${candidate}${candidate.includes("?") ? "&" : "?"}v=${cacheKey}`;
    }
    // Still allow dialog to load via enrollment blob proxy
    return String(editingStudent.student_img_path || "").trim();
  }, [editingStudent, cacheKey]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing || zipping;

  return (
    <div
      className="content payroll-page sm-page msi-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Manage Student Image</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell msi-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaImage />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Manage Student Image</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${metrics.total} student${
                      metrics.total === 1 ? "" : "s"
                    }`
                  : "Select a class to manage student photos"}
                {classSelected
                  ? ` · ${metrics.withPhoto} with photo · ${metrics.withoutPhoto} missing`
                  : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="msi-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className="msi-zip-btn"
              onClick={downloadPhotosZip}
              disabled={
                busy ||
                !classSelected ||
                !photoCheckDone ||
                metrics.withPhoto === 0
              }
              title="Download photos as ZIP"
            >
              <FaDownload aria-hidden />
              <span>{zipping ? "Preparing…" : "Download ZIP"}</span>
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="msi-body">
          {loadClass ? (
            <div className="msi-class-wrap">
              <div className="msi-class-wrap__head">
                <FaGraduationCap aria-hidden />
                <span>Select class & section</span>
              </div>
              <SelectClass
                value={loadClass}
                action={onClassSelect}
                selected_position={selectedClassPosition}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="msi-empty-box">
              <div className="msi-empty-box__icon" aria-hidden>
                <FaUserGraduate />
              </div>
              <p className="msi-empty-box__title">Select a class</p>
              <p className="msi-empty-box__hint">
                Choose class and section to browse and edit student photos in a
                square grid
              </p>
            </div>
          ) : loading ? (
            <div className="msi-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <section className="msi-summary" aria-label="Photo summary">
                <div className="msi-metrics">
                  <article className="msi-metric">
                    <span className="msi-metric__icon msi-metric__icon--total">
                      <FaUsers aria-hidden />
                    </span>
                    <div>
                      <strong>{metrics.total}</strong>
                      <span>Students</span>
                    </div>
                  </article>
                  <article className="msi-metric msi-metric--ok">
                    <span className="msi-metric__icon msi-metric__icon--ok">
                      <FaCheckCircle aria-hidden />
                    </span>
                    <div>
                      <strong>{metrics.withPhoto}</strong>
                      <span>With photo</span>
                    </div>
                  </article>
                  <article className="msi-metric msi-metric--warn">
                    <span className="msi-metric__icon msi-metric__icon--warn">
                      <FaExclamationCircle aria-hidden />
                    </span>
                    <div>
                      <strong>{metrics.withoutPhoto}</strong>
                      <span>Missing</span>
                    </div>
                  </article>
                </div>

                <div className="msi-progress">
                  <div className="msi-progress__head">
                    <span>
                      {photoCheckDone ? "Photo coverage" : "Verifying photos…"}
                    </span>
                    <strong>{metrics.pct}%</strong>
                  </div>
                  <div
                    className="msi-progress__track"
                    role="progressbar"
                    aria-valuenow={metrics.pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <span
                      className="msi-progress__fill"
                      style={{ width: `${metrics.pct}%` }}
                    />
                  </div>
                </div>
              </section>

              <div className="msi-toolbar">
                <div
                  className="msi-filters"
                  role="tablist"
                  aria-label="Photo filter"
                >
                  {FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={photoFilter === opt.id}
                      className={`msi-filter${
                        photoFilter === opt.id ? " is-active" : ""
                      }`}
                      onClick={() => setPhotoFilter(opt.id)}
                    >
                      {opt.label}
                      <span className="msi-filter__count">
                        {filterCounts[opt.id]}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="msi-toolbar__note">
                  Showing {filteredStudents.length} of {students.length}
                </p>
              </div>

              {filteredStudents.length === 0 ? (
                <div className="msi-empty-box msi-empty-box--soft">
                  <div className="msi-empty-box__icon" aria-hidden>
                    <FaImage />
                  </div>
                  <p className="msi-empty-box__title">No students found</p>
                  <p className="msi-empty-box__hint">
                    {searchTerm || photoFilter !== "all"
                      ? "Try a different search or filter."
                      : "This class has no active students."}
                  </p>
                </div>
              ) : (
                <section className="msi-panel" aria-label="Student photo grid">
                  <div className="msi-panel__head">
                    <h2>
                      <FaImage aria-hidden /> Student photos
                    </h2>
                    <span>{filteredStudents.length} items</span>
                  </div>
                  <div className="msi-grid">
                    {filteredStudents.map((student, index) => (
                      <StudentPhotoCard
                        key={student.id || student.student_enrollment_no}
                        student={student}
                        index={index}
                        cacheKey={cacheKey}
                        withPhoto={studentHasPhoto(student)}
                        onEdit={openEditor}
                        onPhotoResult={handlePhotoResult}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      <MdlEditStudentImage
        open={editorOpen}
        student={editingStudent}
        imageUrl={editingImageUrl}
        cacheKey={cacheKey}
        onClose={closeEditor}
        onSaved={handleSaved}
      />
    </div>
  );
}
